import { useGraphStore, type GraphFilters } from '@/stores/graph';
import {
  ENTITY_CLASS_COLORS,
  ENTITY_CLASS_LABELS,
  type LayoutMode,
} from '@/services/api';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { clsx } from 'clsx';
import { EDGE_TYPE_COLORS } from './graphAdapter';

export function GraphControlsPanel() {
  const {
    filters,
    updateFilters,
    stats,
    snapshot,
    vizLoading,
    reload,
    sidebarCollapsed,
    toggleSidebar,
  } = useGraphStore();

  const meta = snapshot?.meta ?? null;

  if (sidebarCollapsed) {
    return (
      <div className="h-full w-10 bg-slate-900 border-r border-slate-700/50 flex flex-col items-center py-3 flex-shrink-0">
        <button
          onClick={toggleSidebar}
          className="p-1.5 text-slate-400 hover:text-white rounded transition-colors"
          title="Expand controls"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-72 bg-slate-900 border-r border-slate-700/50 flex flex-col overflow-hidden flex-shrink-0">
      <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">Graph Explorer</h2>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Knowledge graph visualization
          </p>
        </div>
        <button
          onClick={toggleSidebar}
          className="p-1 text-slate-500 hover:text-white rounded transition-colors"
        >
          <ChevronLeft size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
        {/* Layout mode */}
        <ControlSection title="Layout Mode">
          <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-800 rounded-lg">
            {(['force', 'embedding'] as LayoutMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => updateFilters({ layout: mode })}
                className={clsx(
                  'flex flex-col items-center gap-1 px-3 py-2 rounded-md text-xs font-medium transition-all',
                  filters.layout === mode
                    ? 'bg-teal-600/30 text-teal-300 ring-1 ring-teal-500/50'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50',
                )}
              >
                <span>{mode === 'embedding' ? 'Embedding' : 'Force'}</span>
                <span className="text-[9px] text-slate-500">
                  {mode === 'embedding' ? 'UMAP 2D' : 'ForceAtlas2'}
                </span>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5">
            {filters.layout === 'embedding'
              ? 'Nodes placed by semantic similarity'
              : 'Physics-based force-directed layout'}
          </p>
        </ControlSection>

        {/* Client filter */}
        {stats?.clients && stats.clients.length > 0 && (
          <ControlSection title="Client">
            <select
              className="w-full bg-slate-800 border border-slate-700 rounded-md px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              value={filters.clientId ?? ''}
              onChange={(e) => updateFilters({ clientId: e.target.value || null })}
            >
              <option value="">All clients</option>
              {stats.clients.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </ControlSection>
        )}

        {/* Edge similarity */}
        <ControlSection title="Min Edge Similarity">
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={filters.minEdgeSimilarity}
              onChange={(e) =>
                updateFilters({ minEdgeSimilarity: parseFloat(e.target.value) })
              }
              className="flex-1 accent-teal-400"
            />
            <span className="text-xs font-mono text-teal-400 w-9 text-right">
              {filters.minEdgeSimilarity.toFixed(2)}
            </span>
          </div>
        </ControlSection>

        {/* Structural edges toggle */}
        <label className="flex items-center gap-2 cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.includeStructural}
            onChange={(e) => updateFilters({ includeStructural: e.target.checked })}
            className="accent-teal-400 rounded"
          />
          <span className="text-xs text-slate-300 group-hover:text-white transition-colors">
            Show structural edges
          </span>
        </label>

        {/* Max nodes */}
        <ControlSection title="Max Nodes">
          <input
            type="number"
            min={10}
            max={50000}
            step={100}
            value={filters.maxNodes}
            onChange={(e) =>
              updateFilters({ maxNodes: parseInt(e.target.value, 10) || 2000 })
            }
            className="w-full bg-slate-800 border border-slate-700 rounded-md px-2.5 py-1.5 text-xs text-white font-mono focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </ControlSection>

        {/* Entity class filter */}
        {meta && meta.entity_classes.length > 0 && (
          <ControlSection title="Entity Classes">
            <div className="space-y-1">
              {meta.entity_classes.map((ec) => {
                const color = ENTITY_CLASS_COLORS[ec] ?? '#94a3b8';
                const label = ENTITY_CLASS_LABELS[ec] ?? ec.replace(/_/g, ' ');
                const active = !filters.entityClasses || filters.entityClasses.includes(ec);
                return (
                  <label key={ec} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => {
                        const current = filters.entityClasses ?? meta.entity_classes;
                        const next = e.target.checked
                          ? [...current, ec]
                          : current.filter((c) => c !== ec);
                        updateFilters({
                          entityClasses:
                            next.length === meta.entity_classes.length ? null : next,
                        });
                      }}
                      className="accent-teal-400 rounded"
                    />
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs text-slate-300 group-hover:text-white transition-colors flex-1 truncate capitalize">
                      {label}
                    </span>
                    {stats?.nodes_by_class?.[ec] != null && (
                      <span className="text-[10px] text-slate-500 font-mono">
                        {stats.nodes_by_class[ec]}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </ControlSection>
        )}

        {/* Current view stats */}
        {meta && (
          <ControlSection title="Current View">
            <div className="grid grid-cols-2 gap-2">
              <StatCard label="Nodes" value={meta.node_count.toLocaleString()} />
              <StatCard label="Edges" value={meta.link_count.toLocaleString()} />
            </div>
            {meta.entity_classes.length > 0 && (
              <div className="mt-1 text-[10px] text-slate-500">
                {meta.entity_classes.length} entity classes
              </div>
            )}
          </ControlSection>
        )}

        {/* Edge legend */}
        <ControlSection title="Edge Legend">
          <div className="space-y-1">
            {Object.entries(EDGE_TYPE_COLORS).map(([type, color]) => (
              <div key={type} className="flex items-center gap-2">
                <div
                  className="w-5 h-0.5 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="text-[10px] text-slate-400 capitalize">
                  {type.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </ControlSection>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-slate-700/50">
        <button
          onClick={reload}
          disabled={vizLoading}
          className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 disabled:text-slate-400 text-white text-xs font-medium px-3 py-2 rounded-md transition-colors"
        >
          {vizLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          {vizLoading ? 'Loading...' : 'Reload Graph'}
        </button>
      </div>
    </div>
  );
}

function ControlSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
        {title}
      </h3>
      {children}
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-800 rounded-md px-2.5 py-1.5 text-center">
      <div className="text-sm font-semibold text-white">{value}</div>
      <div className="text-[9px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
